import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const bankAccountKeys = {
  all: ['bankAccounts'],
  lists: () => [...bankAccountKeys.all, 'list'],
  details: () => [...bankAccountKeys.all, 'detail'],
  detail: (id) => [...bankAccountKeys.details(), id],
  byBank: (bankName) => [...bankAccountKeys.all, 'byBank', bankName],
  byCbu: (cbu) => [...bankAccountKeys.all, 'byCbu', cbu],
};

/**
 * Hook para obtener todas las cuentas bancarias activas
 */
export const useBankAccounts = () => {
  return useQuery({
    queryKey: bankAccountKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('bank_accounts');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una cuenta bancaria específica
 * @param {string} id - UUID de la cuenta bancaria
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankAccount = (id, options = {}) => {
  return useQuery({
    queryKey: bankAccountKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`bank_accounts/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener cuentas bancarias por nombre del banco
 * @param {string} bankName - Nombre del banco (enum)
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankAccountsByBank = (bankName, options = {}) => {
  return useQuery({
    queryKey: bankAccountKeys.byBank(bankName),
    queryFn: async () => {
      const response = await apiClient.get(`bank_accounts/by-bank/${bankName}`);
      return response.data;
    },
    enabled: !!bankName,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener cuenta bancaria por CBU
 * @param {string} cbu - CBU de la cuenta bancaria
 * @param {Object} options - Opciones adicionales para la query
 */
export const useBankAccountByCbu = (cbu, options = {}) => {
  return useQuery({
    queryKey: bankAccountKeys.byCbu(cbu),
    queryFn: async () => {
      const response = await apiClient.get(`bank_accounts/by-cbu/${cbu}`);
      return response.data;
    },
    enabled: !!cbu,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva cuenta bancaria
 */
export const useCreateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bankAccountData) => {
      const response = await apiClient.post('bank_accounts', bankAccountData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.lists() });
      if (data.bank_name) {
        queryClient.invalidateQueries({ queryKey: bankAccountKeys.byBank(data.bank_name) });
      }
      if (data.cbu) {
        queryClient.setQueryData(bankAccountKeys.byCbu(data.cbu), data);
      }
    },
    onError: (error) => {
      console.error('Error creating bank account:', error);
    },
  });
};

/**
 * Hook para actualizar una cuenta bancaria existente
 */
export const useUpdateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`bank_accounts/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(bankAccountKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.lists() });
      if (data.bank_name) {
        queryClient.invalidateQueries({ queryKey: bankAccountKeys.byBank(data.bank_name) });
      }
      if (data.cbu) {
        queryClient.setQueryData(bankAccountKeys.byCbu(data.cbu), data);
      }
    },
    onError: (error) => {
      console.error('Error updating bank account:', error);
    },
  });
};

/**
 * Hook para eliminar lógicamente una cuenta bancaria
 */
export const useDeleteBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`bank_accounts/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.lists() });
      queryClient.removeQueries({ queryKey: bankAccountKeys.detail(deletedId) });
      if (data.bank_name) {
        queryClient.invalidateQueries({ queryKey: bankAccountKeys.byBank(data.bank_name) });
      }
      if (data.cbu) {
        queryClient.removeQueries({ queryKey: bankAccountKeys.byCbu(data.cbu) });
      }
    },
    onError: (error) => {
      console.error('Error deleting bank account:', error);
    },
  });
};
